import React from "react";
import Modal from "react-modal";
import { X, ShieldCheck, ArrowRight, AlertCircle } from "lucide-react";

const otpModalStyles = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    display: "grid",
    placeItems: "center",
    zIndex: 1100,
    padding: 16,
    backdropFilter: "blur(4px)",
  },
  content: {
    position: "relative",
    inset: "auto",
    width: "100%",
    maxWidth: 480,
    borderRadius: 28,
    padding: 0,
    border: "none",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
    overflow: "hidden",
    background: "#ffffff",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "'Inter', sans-serif",
  },
};

export default function OtpVerifyModal({
  isOpen,
  phoneE164, // Recibe el número como venga (+523312345678)
  cooldownSec = 30,
  loading = false,
  errorText = "",
  onClose,
  onSubmitCode,
  onResend,
}) {
  const [digits, setDigits] = React.useState(Array(6).fill(""));
  const inputsRef = React.useRef([]);
  const [cooldown, setCooldown] = React.useState(cooldownSec);
  const [resending, setResending] = React.useState(false);

  const formatPhoneInternal = (raw = "") => {
    const cleaned = raw.replace(/[^\d+]/g, "");

    if (!cleaned) return "";

    if (cleaned.startsWith("+52") && cleaned.length >= 12) {
      const rest = cleaned.slice(3); // Los números después del +52
      // Formato: +52 33 3130 8335 (o lo que alcance a mostrar)
      return `+52 ${rest.replace(/(\d{2})(\d{4})(\d{4})/, "$1 $2 $3").trim()}`;
    }
    return cleaned;
  };

  const formattedPhone = React.useMemo(
    () => formatPhoneInternal(phoneE164),
    [phoneE164],
  );
  const code = digits.join("");
  const isComplete = /^[0-9]{6}$/.test(code);

  React.useEffect(() => {
    if (!isOpen) return;
    setDigits(Array(6).fill(""));
    setCooldown(cooldownSec);
    setResending(false);
    setTimeout(() => inputsRef.current?.[0]?.focus?.(), 80);
  }, [isOpen, cooldownSec]);

  React.useEffect(() => {
    if (!isOpen || cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [isOpen, cooldown]);

  const setAt = (idx, val) => {
    setDigits((prev) => {
      const next = [...prev];
      next[idx] = val;
      return next;
    });
  };

  const focusAt = (idx) => inputsRef.current?.[idx]?.focus?.();

  const handleChange = (idx, raw) => {
    const cleaned = String(raw || "").replace(/\D/g, "");
    if (!cleaned) return setAt(idx, "");
    const arr = cleaned.split("").slice(0, 6 - idx);
    setDigits((prev) => {
      const next = [...prev];
      for (let i = 0; i < arr.length; i++) next[idx + i] = arr[i];
      return next;
    });
    focusAt(Math.min(5, idx + arr.length));
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace") {
      if (digits[idx]) return setAt(idx, "");
      if (idx > 0) {
        setAt(idx - 1, "");
        focusAt(idx - 1);
      }
      return;
    }
    if (e.key === "ArrowLeft" && idx > 0) focusAt(idx - 1);
    if (e.key === "ArrowRight" && idx < 5) focusAt(idx + 1);
    if (e.key === "Enter" && isComplete && !loading) onSubmitCode?.(code);
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "");
    if (!text) return;
    e.preventDefault();
    const arr = text.slice(0, 6).split("");
    setDigits(arr.concat(Array(6 - arr.length).fill("")).slice(0, 6));
    focusAt(Math.min(5, arr.length - 1));
  };

  const handleResend = async () => {
    if (cooldown > 0 || resending || loading) return;
    try {
      setResending(true);
      await onResend?.();
      setDigits(Array(6).fill(""));
      setCooldown(cooldownSec);
      setTimeout(() => focusAt(0), 80);
    } finally {
      setResending(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => (!loading ? onClose?.() : null)}
      shouldCloseOnOverlayClick={!loading}
      shouldCloseOnEsc={!loading}
      style={otpModalStyles}
      ariaHideApp={false}
    >
      <div style={{ padding: "40px 32px", position: "relative" }}>
        <button
          type="button"
          onClick={() => (!loading ? onClose?.() : null)}
          style={{
            position: "absolute",
            top: 24,
            right: 24,
            border: 0,
            background: "transparent",
            color: "#9CA3AF",
            cursor: "pointer",
          }}
        >
          <X size={22} />
        </button>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: 24,
          }}
        >
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              backgroundColor: "#F0FDF4",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ShieldCheck size={36} color="#22C55E" strokeWidth={1.5} />
          </div>
        </div>

        <h2
          style={{
            textAlign: "center",
            fontSize: 28,
            fontWeight: 700,
            color: "#111827",
            margin: "0 0 12px",
          }}
        >
          Código Enviado
        </h2>

        <p
          style={{
            textAlign: "center",
            fontSize: 15,
            color: "#6B7280",
            margin: "0 0 32px",
            lineHeight: 1.5,
          }}
        >
          Hemos enviado un código de verificación de 6 dígitos a <br />
          <strong style={{ color: "#111827", letterSpacing: "0.5px" }}>
            {formattedPhone}
          </strong>
        </p>

        <div
          onPaste={handlePaste}
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 12,
            marginBottom: 8,
          }}
        >
          {digits.map((digit, idx) => (
            <input
              key={idx}
              ref={(el) => (inputsRef.current[idx] = el)}
              value={digit}
              inputMode="numeric"
              maxLength={1}
              onChange={(e) => handleChange(idx, e.target.value)}
              onKeyDown={(e) => handleKeyDown(idx, e)}
              disabled={loading}
              style={{
                width: 50,
                height: 62,
                borderRadius: 14,
                border: `2px solid ${errorText ? "#EF4444" : "#E5E7EB"}`,
                textAlign: "center",
                fontSize: 24,
                fontWeight: 700,
                color: "#111827",
                transition: "all 0.2s",
                outline: "none",
                background: loading ? "#F9FAFB" : "#FFF",
                boxShadow: errorText
                  ? "0 0 0 4px rgba(239, 68, 68, 0.1)"
                  : "none",
              }}
            />
          ))}
        </div>

        <div
          style={{
            height: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 16,
          }}
        >
          {errorText && (
            <div
              style={{
                color: "#EF4444",
                fontSize: 14,
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <AlertCircle size={16} />
              {errorText}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => onSubmitCode?.(code)}
          disabled={!isComplete || loading}
          style={{
            width: "100%",
            height: 58,
            borderRadius: 16,
            border: 0,
            background: isComplete ? "#3B82F6" : "#E5E7EB",
            color: "#fff",
            fontSize: 16,
            fontWeight: 600,
            cursor: isComplete && !loading ? "pointer" : "not-allowed",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            transition: "all 0.2s",
          }}
        >
          {loading ? "Verificando..." : "Verificar Código"}
          {!loading && <ArrowRight size={20} />}
        </button>

        <div style={{ marginTop: 24, textAlign: "center" }}>
          <p style={{ fontSize: 14, color: "#6B7280", margin: 0 }}>
            {cooldown > 0 ? (
              <>
                ¿No recibiste el código? Espera{" "}
                <strong style={{ color: "#111827" }}>{cooldown}s</strong>
              </>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                disabled={loading || resending}
                style={{
                  border: 0,
                  background: "transparent",
                  color: "#3B82F6",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 14,
                }}
              >
                {resending ? "Reenviando..." : "Reenviar código ahora"}
              </button>
            )}
          </p>
        </div>
      </div>
    </Modal>
  );
}
